// src/pages/Appointments/AppointmentCalendar.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiClock,
  FiTrendingUp,
  FiArrowLeft,
} from "react-icons/fi";
import Button from "../../components/common/Button";
import { getAppointmentBookingDensityThunk } from "../../store/thunks/appointmentThunks";

const AppointmentCalendar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bookingDensity, loadingDensity } = useSelector(
    (state) => state.appointment
  );
  const { selectedBranch } = useSelector((state) => state.branch);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // month or week

  useEffect(() => {
    loadBookingDensity();
  }, [currentDate, selectedBranch, viewMode]);

  const loadBookingDensity = () => {
    const params = {
      branchCode: selectedBranch,
      viewMode,
    };

    if (viewMode === "month") {
      params.year = currentDate.getFullYear();
      params.month = currentDate.getMonth() + 1;
    } else if (viewMode === "week") {
      params.startDate = getWeekStart(currentDate).toISOString();
      params.endDate = getWeekEnd(currentDate).toISOString();
    }

    dispatch(getAppointmentBookingDensityThunk(params));
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const getWeekEnd = (date) => {
    const start = getWeekStart(date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getBookingCount = (date) => {
    if (!date || !bookingDensity) return 0;
    const dateStr = date.toISOString().split("T")[0];
    return bookingDensity[dateStr] || 0;
  };

  const getBookingDensityColor = (count) => {
    if (count === 0) return "bg-gray-100";
    if (count <= 2) return "bg-green-200";
    if (count <= 5) return "bg-yellow-200";
    if (count <= 8) return "bg-orange-200";
    return "bg-red-200";
  };

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    // Navigate to appointments list filtered by this date
    const dateStr = date.toISOString().split("T")[0];
    navigate(`/appointments/list?date=${dateStr}`);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });

  // Calculate statistics
  const totalAppointments = Object.values(bookingDensity || {}).reduce(
    (sum, count) => sum + count,
    0
  );
  const daysWithAppointments = Object.keys(bookingDensity || {}).length;
  const maxAppointmentsInDay = Math.max(
    ...Object.values(bookingDensity || {}),
    0
  );
  const avgAppointmentsPerDay =
    daysWithAppointments > 0
      ? (totalAppointments / daysWithAppointments).toFixed(1)
      : 0;

  return (
    <div className="container mx-auto p-4 md:p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-neutral-dark hover:text-primary mb-4 transition-colors"
      >
        <FiArrowLeft className="mr-2" />
        Back to Appointments
      </button>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-text mb-1">
          Appointment Calendar
        </h1>
        <p className="text-neutral-dark">
          Visual overview of booking density across dates
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<FiCalendar />}
          label="Total Appointments"
          value={totalAppointments}
          color="blue"
        />
        <StatCard
          icon={<FiClock />}
          label="Days with Bookings"
          value={daysWithAppointments}
          color="green"
        />
        <StatCard
          icon={<FiTrendingUp />}
          label="Peak Day"
          value={maxAppointmentsInDay}
          color="orange"
        />
        <StatCard
          icon={<FiCalendar />}
          label="Avg per Day"
          value={avgAppointmentsPerDay}
          color="purple"
        />
      </div>

      {/* Calendar Controls */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-neutral-light">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-text">{monthName}</h2>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Today
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePreviousMonth}>
                <FiChevronLeft />
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextMonth}>
                <FiChevronRight />
              </Button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {loadingDensity ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-semibold text-neutral-dark py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-2">
              {days.map((date, index) => {
                if (!date) {
                  return (
                    <div key={`empty-${index}`} className="aspect-square" />
                  );
                }

                const count = getBookingCount(date);
                const densityColor = getBookingDensityColor(count);
                const today = isToday(date);
                const selected = isSelected(date);

                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={`
                      aspect-square rounded-lg p-2 transition-all
                      ${densityColor}
                      ${today ? "ring-2 ring-primary" : ""}
                      ${selected ? "ring-2 ring-blue-500" : ""}
                      hover:scale-105 hover:shadow-md
                      flex flex-col items-center justify-center
                      relative
                    `}
                  >
                    <span
                      className={`text-sm font-medium ${
                        today ? "text-primary" : "text-text"
                      }`}
                    >
                      {date.getDate()}
                    </span>
                    {count > 0 && (
                      <span className="text-xs font-semibold mt-1 px-2 py-0.5 bg-white bg-opacity-80 rounded-full">
                        {count}
                      </span>
                    )}
                    {today && (
                      <div className="absolute bottom-1 w-1 h-1 bg-primary rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="p-4 border-t border-neutral-light">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <LegendItem color="bg-gray-100" label="No bookings" />
            <LegendItem color="bg-green-200" label="1-2 bookings" />
            <LegendItem color="bg-yellow-200" label="3-5 bookings" />
            <LegendItem color="bg-orange-200" label="6-8 bookings" />
            <LegendItem color="bg-red-200" label="9+ bookings (Peak)" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-text mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/appointments/list")}
            className="w-full"
          >
            View All Appointments
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/appointments/today")}
            className="w-full"
          >
            Today's Appointments
          </Button>
          <Button
            onClick={() => navigate("/appointments/book")}
            className="w-full"
          >
            Book New Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    orange: "bg-orange-100 text-orange-600",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-neutral-dark">{label}</p>
          <p className="text-2xl font-bold text-text">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Legend Item Component
const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded ${color} border border-neutral-light`} />
    <span className="text-sm text-neutral-dark">{label}</span>
  </div>
);

export default AppointmentCalendar;
